import { useEffect, useState } from "react";
import { BrowserRouter, useLocation } from "react-router";

import { strings } from "./brand/strings";
import { PERSONA_HINT_KEY } from "./nav/PersonaBar";
import { ShellPage } from "./ShellPage";
import { SignInPage } from "./SignInPage";
import {
  beginSignIn,
  CALLBACK_PATH,
  completeSignIn,
  currentSession,
  signOut,
  type Session,
} from "./auth/session";

type View =
  | { kind: "signed-out"; failed: boolean }
  | { kind: "completing" }
  | { kind: "signed-in"; session: Session };

function initialView(): View {
  if (window.location.pathname === CALLBACK_PATH) {
    return { kind: "completing" };
  }
  const session = currentSession();
  return session === null ? { kind: "signed-out", failed: false } : { kind: "signed-in", session };
}

/**
 * Scroll-to-top on every pathname change (M5 P5c, his #9): a "View all" link or any
 * deep link must land at the top of the register, never wherever the previous page
 * happened to be scrolled. Fixed once here at the router level, not per link.
 */
export function ScrollToTop(): null {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

/**
 * The whole of the client's routing: the gate, the callback, and the signed-in
 * application with its routes.
 *
 * The router (react-router, declarative, the choice ADR-022 section 8 names) arrives
 * now, at the first navigable register, which is the point at which deep links and
 * back-button behaviour became product decisions rather than defaults.
 */
export function App(): React.JSX.Element {
  const [view, setView] = useState<View>(initialView);

  // The persona bar's handoff (Ruling 17): after the provider's post-logout redirect
  // lands on the gate, the stored hint starts a REAL authorization with login_hint.
  // The hint is consumed, never a credential; the provider still authenticates.
  useEffect(() => {
    if (view.kind !== "signed-out") {
      return;
    }
    const hint = sessionStorage.getItem(PERSONA_HINT_KEY);
    sessionStorage.removeItem(PERSONA_HINT_KEY);
    if (hint === null) {
      return;
    }
    beginSignIn(undefined, { loginHint: hint }).catch(() => undefined);
  }, [view.kind]);

  useEffect(() => {
    if (view.kind !== "completing") {
      return;
    }
    const query = new URLSearchParams(window.location.search);
    completeSignIn(query)
      .then((session) => {
        // The code and the state are single-use and have no business staying in the
        // address bar, in the history, or in a referrer header.
        window.history.replaceState({}, "", "/");
        setView({ kind: "signed-in", session });
      })
      .catch(() => {
        window.history.replaceState({}, "", "/");
        setView({ kind: "signed-out", failed: true });
      });
  }, [view.kind]);

  if (view.kind === "completing") {
    return (
      <div className="q-gate">
        <div className="q-sky" aria-hidden="true">
          <div className="q-stars q-stars--1" />
        </div>
        <main className="q-gate__main">
          <h1 className="q-wordmark q-gate__wordmark">{strings.productName}</h1>
          <p className="q-gate__descriptor" role="status">
            {strings.completing.message}
          </p>
        </main>
      </div>
    );
  }

  if (view.kind === "signed-in") {
    return (
      <BrowserRouter>
        <ScrollToTop />
        <ShellPage
          session={view.session}
          onSignOut={() => {
            void signOut().catch(() => {
              setView({ kind: "signed-out", failed: false });
            });
          }}
        />
      </BrowserRouter>
    );
  }

  return (
    <SignInPage
      failed={view.failed}
      onSignIn={() => {
        void beginSignIn().catch(() => {
          setView({ kind: "signed-out", failed: true });
        });
      }}
    />
  );
}
